import React from 'react';

/**
 * CypGuardianContactLines
 *
 * The "Parent/carer: Name, Relationship, email" line(s) that sit under a
 * child's name in the CYP ADHD page headers.
 *
 * Why this exists
 * ---------------
 * An adult patient has their own account, so the header shows their email
 * under their name. A child does not: the person the clinic contacts is the
 * parent/carer, so the header names them instead (HK testing, 2 Sept 2026:
 * guardian contact details must be visible without opening Manage parents and
 * school). The school representative is deliberately NOT shown here (Eve, 3
 * Sept): their details live in Manage parents and school and on the school
 * forms card.
 *
 * The same line belongs on every header for the child, so a clinician sees
 * one patient, not two. It started life inside the Patient Overview header
 * only; the Documents page kept the adult layout and so showed a blank line
 * where the email would have been (tester feedback, Oct 2026). Sharing one
 * component means the two headers cannot drift apart again.
 *
 * Data shape
 * ----------
 * `informants` is the patient record's informants map, keyed by informant id.
 * Each entry carries informantRole ('legalGuardian' or 'schoolRep'),
 * informantName, informantRelationshipToPatient and informantEmail. Only the
 * legal guardians are rendered, one line each (a child can have more than one
 * parent/carer on record). Renders nothing when there are none, so the
 * caller does not need its own guard.
 *
 * @param {object} props
 * @param {Object} [props.informants] The patient record's informants map.
 */
export default function CypGuardianContactLines({ informants }) {
  const guardians = Object.entries(informants || {})
    .filter(([, entry]) => entry?.informantRole === 'legalGuardian');

  if (guardians.length === 0) return null;

  return (
    <div className="self-stretch flex flex-col gap-0.5 mt-1">
      {guardians.map(([informantId, entry]) => {
        // "Name, Relationship", dropping whichever part is missing so the
        // line never shows a stray comma.
        const detail = [
          entry?.informantName,
          entry?.informantRelationshipToPatient,
        ].filter(Boolean).join(', ');
        return (
          <div key={informantId} className="text-sm text-slate-700">
            <span className="font-semibold">Parent/carer:</span>{' '}
            {detail || 'Not named'}
            {entry?.informantEmail ? (
              <>
                {', '}
                <a
                  className="text-primary underline"
                  href={`mailto:${entry.informantEmail}`}
                >
                  {entry.informantEmail}
                </a>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
